import { FormEvent, useCallback, useEffect, useId, useRef, useState } from "react";
import { ApiError } from "../../lib/api-client";
import { createEvent, getEvents } from "../../lib/api-event";
import { getFamilyMembers, getMyFamilies } from "../../lib/api-family";
import type { FamilyEvent, EventType } from "../../types/event";
import type { FamilyMember } from "../../types/family";
import { Icon } from "../../components/Icon";
import { DeleteEventDialog, EditEventDialog } from "./EventLifecycleDialogs";

const eventTypes: Array<{ value: EventType; label: string }> = [
  { value: "birthday", label: "День рождения" },
  { value: "anniversary", label: "Годовщина" },
  { value: "holiday", label: "Праздник" },
  { value: "other", label: "Другое" },
];

type State =
  | { status: "loading" }
  | { status: "error" }
  | { status: "loaded"; events: FamilyEvent[]; members: FamilyMember[] };

type Notice = { kind: "success" | "error"; message: string };

function moscowToday(): string {
  const parts = new Intl.DateTimeFormat("en", {
    timeZone: "Europe/Moscow",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const value = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? "";
  return `${value("year")}-${value("month")}-${value("day")}`;
}

function displayDate(date: string): string {
  return new Intl.DateTimeFormat("ru-RU", {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(`${date}T00:00:00Z`));
}

function errorMessage(error: unknown): string {
  if (error instanceof ApiError && error.status === 403) {
    return "Выберите активного участника текущей семьи.";
  }
  if (error instanceof ApiError && error.code === "VALIDATION_FAILED") {
    return "Проверьте название, тип и дату события.";
  }
  return "Не удалось создать событие. Попробуйте ещё раз.";
}

function EventCard({
  event, highlighted, onEdit, onDelete,
}: {
  event: FamilyEvent;
  highlighted: boolean;
  onEdit: (event: FamilyEvent) => void;
  onDelete: (event: FamilyEvent) => void;
}) {
  const type = eventTypes.find((item) => item.value === event.type)?.label;
  return (
    <li
      aria-label={highlighted ? `Выбранное событие: ${event.title}` : undefined}
      className={`rounded-[24px] bg-white p-5 shadow-[0_14px_36px_rgba(35,33,25,0.07)] ${
        highlighted ? "ring-2 ring-action ring-offset-2 ring-offset-canvas" : ""
      }`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-action">{type}</p>
          <h2 className="mt-1 text-xl font-extrabold tracking-[-0.035em] text-[#171714]">{event.title}</h2>
        </div>
        <time dateTime={event.date} className="shrink-0 rounded-full bg-surface-subtle px-3 py-1.5 text-xs font-bold text-content">
          {displayDate(event.date)}
        </time>
      </div>
      {event.description && <p className="mt-3 text-sm leading-6 text-muted">{event.description}</p>}
      {event.member && (
        <div className="mt-4 flex items-center gap-3 border-t border-line pt-4">
          <span className="flex h-9 w-9 items-center justify-center overflow-hidden rounded-xl bg-action-soft text-action">
            {event.member.avatar_url ? <img src={event.member.avatar_url} alt="" className="h-full w-full object-cover" /> : <Icon name="family" size={18} />}
          </span>
          <span className="text-sm font-semibold text-[#171714]">{event.member.name}</span>
        </div>
      )}
      <div className="mt-4 grid grid-cols-2 gap-2 border-t border-line pt-4">
        <button type="button" onClick={() => onEdit(event)} className="h-10 rounded-xl bg-surface-subtle text-sm font-bold text-content">Редактировать</button>
        <button type="button" onClick={() => onDelete(event)} className="h-10 rounded-xl bg-[#FEF2F2] text-sm font-bold text-[#B91C1C]">Удалить</button>
      </div>
    </li>
  );
}

export function EventsPage() {
  const requestedEventID = new URLSearchParams(window.location.search).get("event");
  const [state, setState] = useState<State>({ status: "loading" });
  const [formOpen, setFormOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [title, setTitle] = useState("");
  const [type, setType] = useState<EventType>("birthday");
  const [date, setDate] = useState("");
  const [description, setDescription] = useState("");
  const [memberID, setMemberID] = useState("");
  const [editing, setEditing] = useState<FamilyEvent | null>(null);
  const [deleting, setDeleting] = useState<FamilyEvent | null>(null);
  const requestID = useRef(0);
  const titleID = useId();
  const typeID = useId();
  const dateID = useId();
  const descriptionID = useId();
  const memberIDField = useId();

  const load = useCallback(async () => {
    const currentRequest = ++requestID.current;
    setState({ status: "loading" });
    try {
      const [events, families] = await Promise.all([getEvents(), getMyFamilies()]);
      const members = families[0] ? await getFamilyMembers(families[0].id) : [];
      if (currentRequest === requestID.current) {
        setState({ status: "loaded", events, members });
      }
    } catch {
      if (currentRequest === requestID.current) setState({ status: "error" });
    }
  }, []);

  useEffect(() => {
    void load();
    return () => {
      requestID.current += 1;
    };
  }, [load]);

  const openForm = () => {
    setNotice(null);
    setFormError(null);
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setFormError(null);
  };

  const resetForm = () => {
    setTitle("");
    setType("birthday");
    setDate("");
    setDescription("");
    setMemberID("");
  };

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmedTitle = title.trim();
    const trimmedDescription = description.trim();
    if (!trimmedTitle || trimmedTitle.length > 255) {
      setFormError("Введите название длиной до 255 символов.");
      return;
    }
    if (!date || date < moscowToday()) {
      setFormError("Выберите сегодняшнюю или будущую дату.");
      return;
    }
    if (trimmedDescription.length > 2000) {
      setFormError("Описание должно быть не длиннее 2000 символов.");
      return;
    }

    setSubmitting(true);
    setFormError(null);
    setNotice(null);
    try {
      await createEvent({
        title: trimmedTitle,
        type,
        date,
        description: trimmedDescription || undefined,
        member_id: memberID || undefined,
      });
    } catch (error) {
      setFormError(errorMessage(error));
      setSubmitting(false);
      return;
    }

    resetForm();
    setFormOpen(false);
    try {
      const events = await getEvents();
      setState((current) =>
        current.status === "loaded" ? { ...current, events } : current,
      );
      setNotice({ kind: "success", message: "Событие создано." });
    } catch {
      setNotice({
        kind: "error",
        message: "Событие создано, но обновить список не удалось.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const finishLifecycle = async (action: "update" | "delete", error?: unknown) => {
    setEditing(null);
    setDeleting(null);
    try {
      const events = await getEvents();
      setState((current) =>
        current.status === "loaded" ? { ...current, events } : current,
      );
      if (error instanceof ApiError && error.status === 403) {
        setNotice({ kind: "error", message: "Изменять это событие может только создатель или администратор." });
      } else if (error) {
        setNotice({ kind: "error", message: "Не удалось выполнить действие. Список обновлён." });
      } else {
        setNotice({ kind: "success", message: action === "update" ? "Событие обновлено." : "Событие удалено." });
      }
    } catch {
      setNotice({ kind: "error", message: "Не удалось обновить актуальный список событий." });
    }
  };

  return (
    <div className="flex flex-1 flex-col px-5 pb-8 pt-7 sm:px-8 lg:px-10 lg:py-10 xl:px-14">
      <header className="mb-7 flex items-end justify-between gap-4">
        <div>
          <p className="mb-1 text-[11px] font-bold uppercase tracking-[0.18em] text-action">Семейный календарь</p>
          <h1 className="text-[32px] font-extrabold leading-none tracking-[-0.055em] text-[#171714] lg:text-[46px]">События</h1>
        </div>
        {state.status === "loaded" && !formOpen && (
          <button type="button" onClick={openForm} className="flex h-11 items-center gap-2 rounded-full bg-action px-4 text-sm font-bold text-white shadow-[0_8px_18px_rgba(194,65,12,0.28)]">
            <Icon name="plus" size={19} /> Создать
          </button>
        )}
      </header>

      {notice && (
        <div role={notice.kind === "error" ? "alert" : "status"} className={`mb-4 flex items-center justify-between gap-3 rounded-2xl px-4 py-3 text-sm font-semibold ${notice.kind === "error" ? "bg-danger-soft text-danger" : "bg-warning-soft text-warning"}`}>
          <span>{notice.message}</span>
          {notice.kind === "error" && <button type="button" onClick={() => void load()} className="underline underline-offset-2">Обновить</button>}
        </div>
      )}
      {state.status === "loaded" && requestedEventID && !state.events.some((event) => event.id === requestedEventID) && (
        <div role="status" className="mb-4 rounded-2xl bg-warning-soft px-4 py-3 text-sm font-semibold text-warning">
          Событие из напоминания больше недоступно. Сохранённые название и дата остаются в напоминании.
        </div>
      )}

      {state.status === "loading" && <div className="flex flex-1 items-center justify-center py-20" aria-label="Загрузка событий"><div className="h-9 w-9 animate-spin rounded-full border-[3px] border-action border-t-transparent" /></div>}
      {state.status === "error" && <div className="flex flex-1 flex-col items-center justify-center rounded-[28px] bg-white px-7 py-16 text-center"><h2 className="text-2xl font-extrabold">Не удалось загрузить события</h2><p className="mt-2 text-sm text-muted">Проверьте подключение и попробуйте снова.</p><button type="button" onClick={() => void load()} className="mt-6 rounded-full bg-content px-7 py-3 text-sm font-semibold text-white">Повторить</button></div>}

      {state.status === "loaded" && formOpen && (
        <form aria-label="Новое событие" onSubmit={(event) => void submit(event)} noValidate className="mb-6 rounded-[26px] bg-white p-5 shadow-[0_14px_36px_rgba(35,33,25,0.07)] sm:p-6">
          <fieldset disabled={submitting} className="grid gap-4 border-0 p-0 sm:grid-cols-2">
            <legend className="mb-4 text-xl font-extrabold text-[#171714]">Новое событие</legend>
            <label htmlFor={titleID} className="text-sm font-bold">Название<input id={titleID} value={title} onChange={(event) => setTitle(event.target.value)} maxLength={255} required className="form-control mt-2 w-full rounded-2xl border border-line px-4 py-3" /></label>
            <label htmlFor={typeID} className="text-sm font-bold">Тип<select id={typeID} value={type} onChange={(event) => setType(event.target.value as EventType)} className="form-control mt-2 w-full rounded-2xl border border-line bg-white px-4 py-3">{eventTypes.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</select></label>
            <label htmlFor={dateID} className="text-sm font-bold">Дата<input id={dateID} type="date" min={moscowToday()} value={date} onChange={(event) => setDate(event.target.value)} required className="form-control mt-2 w-full rounded-2xl border border-line px-4 py-3" /></label>
            <label htmlFor={memberIDField} className="text-sm font-bold">Участник<select id={memberIDField} value={memberID} onChange={(event) => setMemberID(event.target.value)} className="form-control mt-2 w-full rounded-2xl border border-line bg-white px-4 py-3"><option value="">Общее событие</option>{state.members.map((member) => <option key={member.id} value={member.id}>{member.name}{member.is_current ? " · Вы" : ""}</option>)}</select></label>
            <label htmlFor={descriptionID} className="text-sm font-bold sm:col-span-2">Описание (опционально)<textarea id={descriptionID} value={description} onChange={(event) => setDescription(event.target.value)} maxLength={2000} rows={3} className="form-control mt-2 w-full resize-none rounded-2xl border border-line px-4 py-3" /></label>
            {formError && <div role="alert" className="rounded-xl bg-danger-soft px-4 py-3 text-sm font-semibold text-danger sm:col-span-2">{formError}</div>}
            <div className="flex gap-3 sm:col-span-2 sm:justify-end">
              <button type="button" onClick={closeForm} className="h-11 rounded-xl bg-surface-subtle px-5 text-sm font-bold text-content">Отмена</button>
              <button type="submit" className="h-11 rounded-xl bg-action px-5 text-sm font-bold text-white disabled:bg-surface-disabled">{submitting ? "Создаём…" : "Создать событие"}</button>
            </div>
          </fieldset>
        </form>
      )}

      {state.status === "loaded" && state.events.length === 0 && !formOpen && <div className="flex flex-1 flex-col items-center justify-center rounded-[28px] bg-white px-7 py-16 text-center"><div className="flex h-20 w-20 items-center justify-center rounded-[26px] bg-action-soft text-action"><Icon name="calendar" size={36} /></div><h2 className="mt-6 text-2xl font-extrabold">Событий пока нет</h2><p className="mt-2 max-w-sm text-sm leading-6 text-muted">Добавьте день рождения, годовщину или общий семейный праздник.</p><button type="button" onClick={openForm} className="mt-6 rounded-full bg-action px-7 py-3 text-sm font-bold text-white">Создать событие</button></div>}
      {state.status === "loaded" && state.events.length > 0 && <ol className="grid grid-cols-1 gap-4 xl:grid-cols-2">{state.events.map((event) => <EventCard key={event.id} event={event} highlighted={event.id === requestedEventID} onEdit={setEditing} onDelete={setDeleting} />)}</ol>}
      {state.status === "loaded" && editing && <EditEventDialog key={editing.id} event={editing} members={state.members} onClose={() => setEditing(null)} onComplete={(error) => finishLifecycle("update", error)} />}
      {deleting && <DeleteEventDialog event={deleting} onClose={() => setDeleting(null)} onComplete={(error) => finishLifecycle("delete", error)} />}
    </div>
  );
}
